import { ReactElement, useEffect, useMemo } from 'react';
import { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { IntercomProvider } from 'react-use-intercom';
import { ApolloClient, ApolloProvider, InMemoryCache } from '@apollo/client';
import Head from 'next/head';

// FIXME: Get clarification on why this is imported here and not at the top
// of the file.
import '@reach/tooltip/styles.css'; // fixes @reach/tooltip styles not found warning
import { UniversalNextLink } from '@reckon-web/next-link';
import { DrawerProvider } from '@reckon-web/drawer';
import { ToastProvider, useToasts } from '@reckon-web/toast';
import { Flex } from '@reckon-web/flex';
import { Core } from '@reckon-web/core';

import {
  AuthProvider,
  apolloLinkMiddleware,
  signin,
  useAuth,
  useUser,
} from '../components/auth';
import { typePolicies } from '../types/type-policies';
import { result as introspectionQueryResultData } from '../../__generated__/ts-gql/@introspection';
import { Header } from '../components/header';
import { IntercomComponent } from '../components/Intercom';
import { TaskDrawerOpener } from '../components/task/drawer-opener';
import { TaskDrawerProvider } from '../components/task/task-drawer-context';
import { ViewJobDialog } from '../components/job/view-job-dialog';
import { NewJobDialogProvider } from '../components/job/new-job-dialog';
import { VisiblePropertiesProvider } from '../components/common/visible-properties';
import { VisibleFiltersProvider } from '../components/common/visible-filters';
import { workflowPalette } from '../theme';

const possibleTypes: Record<string, string[]> = {};

introspectionQueryResultData.__schema.types.forEach((supertype: any) => {
  if (supertype.possibleTypes) {
    possibleTypes[supertype.name] = supertype.possibleTypes.map(
      (subtype: any) => subtype.name
    );
  }
});

let ApolloClientProvider = ({ children }: { children: ReactElement }) => {
  let user = useUser();
  const tenant = user.profile.currentTenantId;
  const { addToast } = useToasts();

  const handleOnNetworkError = () => {
    addToast({ title: 'Failed to fetch', tone: 'critical' });
  };

  const client = useMemo(() => {
    return new ApolloClient({
      cache: new InMemoryCache({
        possibleTypes,
        typePolicies,
      }),
      link: apolloLinkMiddleware(user, handleOnNetworkError),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

let RedirectForAuth = ({ children }: { children: ReactElement }) => {
  let router = useRouter();
  let auth = useAuth();
  useEffect(() => {
    if (auth.type === 'unauthenticated') {
      signin();
    }
  }, [auth, router.pathname]);

  if (!auth.user) {
    return null;
  }
  return <ApolloClientProvider>{children}</ApolloClientProvider>;
};

// declare outside the component to avoid rerenders
const themeOverrides = {
  colors: { variant: workflowPalette.map((x) => x.value) },
};

export default function App({ Component, pageProps, router }: AppProps) {
  return (
    <AuthProvider>
      <Head>
        <title>Workflow</title>
        <link
          rel="apple-touch-icon"
          sizes="60x60"
          href="/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicon-16x16.png"
        />
        <link rel="manifest" href="/site.webmanifest" />
        <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#5bbad5" />
        <meta name="msapplication-TileColor" content="#da532c" />
        <meta name="theme-color" content="#ffffff" />
      </Head>
      <ToastProvider>
        <RedirectForAuth>
          <DrawerProvider>
            <TaskDrawerProvider>
              <Core linkComponent={UniversalNextLink} theme={themeOverrides}>
                {router.pathname === '/login/oauth2/code/cognito2' ? (
                  <Component {...pageProps} />
                ) : (
                  <VisiblePropertiesProvider>
                    <VisibleFiltersProvider>
                      <NewJobDialogProvider>
                        <IntercomProvider appId="c9m53fof">
                          <Flex direction="column" height="100vh">
                            <Header />
                            <Component {...pageProps} />
                            <ViewJobDialog />
                            <TaskDrawerOpener />
                            <IntercomComponent />
                          </Flex>
                        </IntercomProvider>
                      </NewJobDialogProvider>
                    </VisibleFiltersProvider>
                  </VisiblePropertiesProvider>
                )}
              </Core>
            </TaskDrawerProvider>
          </DrawerProvider>
        </RedirectForAuth>
      </ToastProvider>
    </AuthProvider>
  );
}
